<template>
  <BaseModal
    id="custom-invoice-modal"
    :value="value"
    :clickOutsideClose="false"
    @input="$emit('input', $event)"
  >
    <div class="invoice-modal-content p-8">
      <h2 class="text-2xl font-bold mb-6 text-gray-800">
        Custom Invoice Options
      </h2>

      <div class="grid grid-cols-2 gap-6 mb-4">
        <!-- Left Column -->
        <div class="space-y-4">
          <!-- Language Selection -->
          <div class="form-group">
            <label class="form-label font-semibold text-gray-700 mb-2 block">
              Language *
            </label>
            <div class="flex gap-2">
              <label class="radio-option-compact">
                <input
                  type="radio"
                  v-model="options.language"
                  value="english"
                  class="mr-2"
                />
                <span>English</span>
              </label>
              <label class="radio-option-compact">
                <input
                  type="radio"
                  v-model="options.language"
                  value="spanish"
                  class="mr-2"
                />
                <span>Spanish</span>
              </label>
            </div>
          </div>

          <!-- Orientation -->
          <div class="form-group">
            <label class="form-label font-semibold text-gray-700 mb-2 block">
              Orientation *
            </label>
            <div class="flex gap-2">
              <label class="radio-option-compact">
                <input
                  type="radio"
                  v-model="options.orientation"
                  value="vertical"
                  class="mr-2"
                />
                <span>Vertical</span>
              </label>
              <label class="radio-option-compact">
                <input
                  type="radio"
                  v-model="options.orientation"
                  value="horizontal"
                  class="mr-2"
                />
                <span>Horizontal</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Right Column - Display Options -->
        <div class="form-group">
          <label class="form-label font-semibold text-gray-700 mb-2 block">
            Display Options
          </label>
          <div class="space-y-2">
            <label class="checkbox-option-compact">
              <input type="checkbox" v-model="options.showBrand" class="mr-2" />
              <span>Show Brand</span>
            </label>
            <label class="checkbox-option-compact">
              <input
                type="checkbox"
                v-model="options.showListPrice"
                class="mr-2"
              />
              <span>Show List Price</span>
            </label>
            <label class="checkbox-option-compact">
              <input type="checkbox" v-model="options.showImage" class="mr-2" />
              <span>Show Product Images</span>
            </label>
            <label class="checkbox-option-compact">
              <input
                type="checkbox"
                v-model="options.showUnificado"
                class="mr-2"
              />
              <span>Show Unificado</span>
            </label>
            <label class="checkbox-option-compact">
              <input
                type="checkbox"
                v-model="options.showSaleTerms"
                class="mr-2"
              />
              <span>Show Sale Terms</span>
            </label>
            <label class="checkbox-option-compact">
              <input
                type="checkbox"
                v-model="options.showOrigin"
                class="mr-2"
              />
              <span>Show Origin</span>
            </label>
            <label class="checkbox-option-compact">
              <input
                type="checkbox"
                v-model="options.showForwarder"
                class="mr-2"
              />
              <span>Show Forwarder Information</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        class="error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
      >
        {{ error }}
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          @click="$emit('input', false)"
          class="btn-cancel px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <a
          :href="customOrderUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-generate px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-semibold inline-flex items-center justify-center"
        >
          Generate Invoice
        </a>
      </div>
    </div>
  </BaseModal>
</template>

<script>
export default {
  name: "CustomInvoiceModal",
  props: {
    value: {
      type: Boolean,
      required: true
    },
    orderNumber: {
      type: [String, Number],
      required: true
    },
    orderData: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      options: {
        language: "english",
        orientation: "vertical",
        showBrand: true,
        showListPrice: false,
        showImage: false,
        showUnificado: false,
        showSaleTerms: true,
        showOrigin: true,
        showForwarder: false
      },
      generating: false,
      error: null
    };
  },
  computed: {
    customOrderUrl() {
      const baseUrl = `${this.$store.getters["general/getActiveStore"].endpoint}/orders/custom_order_print/${this.orderNumber}-${this.orderData.validator}`;
      const params = new URLSearchParams({
        language: this.options.language,
        orientation: this.options.orientation,
        showBrand: this.options.showBrand ? "1" : "0",
        showListPrice: this.options.showListPrice ? "1" : "0",
        showImage: this.options.showImage ? "1" : "0",
        showUnificado: this.options.showUnificado ? "1" : "0",
        showSaleTerms: this.options.showSaleTerms ? "1" : "0",
        showOrigin: this.options.showOrigin ? "1" : "0",
        showForwarder: this.options.showForwarder ? "1" : "0"
      });
      return `${baseUrl}?${params.toString()}`;
    }
  },
  methods: {
    async generateInvoice() {
      this.generating = true;
      this.error = null;

      try {
        console.log("Generating custom invoice with options:", this.options);
        console.log("Order number:", this.orderNumber);

        // TODO: Implement API call to generate custom invoice
        // const response = await generateCustomInvoice(this.orderNumber, this.options);

        // Simulate API call for now
        await new Promise(resolve => setTimeout(resolve, 1000));

        this.$toast.open({
          message: "Custom invoice generated successfully",
          type: "success",
          position: "top-center"
        });

        // TODO: Open PDF in new window or download
        // window.open(response.pdfUrl, '_blank');

        this.$emit("invoice-generated", this.options);
        this.$emit("input", false);
      } catch (err) {
        console.error("Error generating custom invoice:", err);
        this.error =
          err.response?.data?.message || "Failed to generate invoice";
        this.$toast.open({
          message: this.error,
          type: "error",
          position: "top-center"
        });
      } finally {
        this.generating = false;
      }
    }
  }
};
</script>

<style lang="postcss">
#custom-invoice-modal .modal-content {
  max-width: 700px !important;
  max-height: 85vh !important;
  height: auto !important;
  overflow-y: auto;
}

.invoice-modal-content {
  @apply bg-white rounded-lg;
}

.form-group {
  @apply mb-4;
}

.radio-option,
.checkbox-option {
  @apply flex items-center cursor-pointer px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors;
}

.radio-option-compact,
.checkbox-option-compact {
  @apply flex items-center cursor-pointer px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors;
}

.radio-option input[type="radio"],
.checkbox-option input[type="checkbox"],
.radio-option-compact input[type="radio"],
.checkbox-option-compact input[type="checkbox"] {
  @apply cursor-pointer;
  width: 16px;
  height: 16px;
}

.checkbox-option,
.checkbox-option-compact {
  @apply bg-gray-50;
}

.btn-generate:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.error-message {
  @apply animate-pulse;
}
</style>
